import {getLayoutData, getPages, getPagesWithVariables} from "@/pages/api/api";
import {useBranch} from "@/hooks/useBranch";
import {useMemo} from "react";
import {
	CARD_BLOCK_QUERY,
	GLOBAL_BASED_BANNER_QUERY,
	SEE_ALSO_INFO_BLOCK_QUERY,
	SEO_QUERY,
	STATE_QUERY,
	WHAT_WE_DO_CATEGORY_QUERY,
	WHAT_WE_DO_SINGLE_ACTION_QUERY
} from "@/pages/api/fragments";
import useSetBannerData from "@/hooks/useSetBannerData";
import ExtendedContent from "@/components/common/ExtendedContent";
import {getNewsUrl} from "@/utils/utils";
import {useRouter} from "next/router";
import ArticlesWithPagination from "@/components/articlesWithPagination/ArticlesWithPagination";
import SeeAlso, {getSeeAlsoData} from "@/components/SeeAlso";
import {CONTENT_TYPES, GLOBAL_STATE} from "@/constants";
import useGlobals from "@/hooks/useGlobals";
import PreviewTag from "@/components/common/PreviewTag";


function useGetCurrentStateData() {
	const allStates = useGlobals(state => state.states)
	const {selectedOption} = useBranch();

	return allStates.find(state => (state?.handle || '') === selectedOption)
}

export default function News({data, allNews, locale, menu, preview = false}) {
	const {selectedOption} = useBranch();
	const router = useRouter()
	const state = useGetCurrentStateData()
	const shouldDisplayGlobalNews = state?.shouldDisplayGlobalNews || false
	const content = data?.content || []
	const horizontalCards = data?.isHorizontalAspect || false;
	const categories = data?.categories || []
	const bannerData = useSetBannerData(data)
	const seeAlsoInfo = data?.seeAlsoInfo || {header: '', variant: ''}
	const stateToFilterBy = shouldDisplayGlobalNews ? GLOBAL_STATE : selectedOption;
	const formattedSeeAlsoData = useMemo(() => getSeeAlsoData(data), [data])

	const filteredNews = useMemo(() => {
		return allNews.filter((item) => {
			return item?.state?.handle?.toLowerCase() === stateToFilterBy
		})
	}, [stateToFilterBy, allNews]);

	const newsCards = useMemo(() => {
		return filteredNews.map((item) => {
			const categories = item?.chooseCategories || [];
			const formattedCategories = categories.map((el, key) => key < categories.length - 1 ? el.categoryName.toLowerCase().concat(', ') : el.categoryName.toLowerCase())
			return {
				...item,
				card: {
					...item?.card,
					supportHeader: formattedCategories || ''
				},
				url: getNewsUrl(item)
			}
		})
	}, [filteredNews])

	return (
		<>
			<PreviewTag visible={preview}/>
			<ExtendedContent content={content} globalBannerData={bannerData}/>
			<ArticlesWithPagination allArticles={newsCards} locale={locale} data={newsCards} categories={categories}
				horizontalCards={horizontalCards}/>
			<SeeAlso
				header={seeAlsoInfo.header}
				variant={seeAlsoInfo.variant}
				data={formattedSeeAlsoData}
			/>
		</>
	)
}

const NEWS_META_TYPE = '_allNewsMeta'
const ALL_NEWS_TYPE = 'allNews'
const ALL_NEWS_PAGE_TYPE = 'allNewsPage'
const ALL_CATEGORIES_TYPE = 'allCategories'

const ALL_NEWS_PAGE_QUERY = `
	query GetAllNewsPageData($locale: SiteLocale) {
		${ALL_NEWS_PAGE_TYPE}(
			locale: $locale
		) {
			__typename
			slug 
			${SEO_QUERY}
			isHorizontalAspect
			content {
				__typename
				${GLOBAL_BASED_BANNER_QUERY}
			}
			seeAlsoInfo{
				${SEE_ALSO_INFO_BLOCK_QUERY}
			}
			seeAlsoLinks {
				__typename
				... on ${CONTENT_TYPES.WHAT_WE_DO_SINGLE_ACTION} {
					${WHAT_WE_DO_SINGLE_ACTION_QUERY}
				}
				... on ${CONTENT_TYPES.WHAT_WE_DO_CATEGORY} {
					${WHAT_WE_DO_CATEGORY_QUERY}
				}
				... on ${CONTENT_TYPES.SINGLE_PAGE} {
					__typename
					id
					pageType
					slug
					card{
						header
						description
					}
				}
			}
		}
		
	}
`;

const NEWS_META_QUERY = `
	query GetNewsCount($locale: SiteLocale) {
    	${NEWS_META_TYPE}(locale: $locale) {
     		count
    	}
  	}
`;

const ALL_NEWS_QUERY = `
	query GetNewsData($slug: String, $locale: SiteLocale, $skip: IntType)  {
		${ALL_NEWS_TYPE}(
			locale: $locale, 
			orderBy: _createdAt_DESC, 
			filter: {slug: {eq: $slug}},
			first: 500,
		    skip: $skip
		) {
			id
			_createdAt
			state: chooseState {
				${STATE_QUERY}
			}
			slug
			title
			card {
				${CARD_BLOCK_QUERY}
			}
			chooseCategories {
				id
				categoryName
				tag
			}
        }
    }
`

const CATEGORIES_QUERY = `
	query GetCategoriesData($locale: SiteLocale) {
		${ALL_CATEGORIES_TYPE}(
			locale: $locale,
			first: 500
		) {
            id
            name: categoryName
            urlParameter
		}
	}
`;

export async function getStaticProps(context) {
	const locale = context?.locale || 'pl';
	const preview = Boolean(context?.preview || false)
	const data = await getPages(ALL_NEWS_PAGE_TYPE, ALL_NEWS_PAGE_QUERY, locale, '', preview);
	const categories = await getPages(ALL_CATEGORIES_TYPE, CATEGORIES_QUERY, locale, '', preview);
	const allNews = await getAllNews(locale, preview)
	const layout = await getLayoutData(locale, preview)

	return {
		props: {
			data: {
				...data,
				categories
			},
			allNews,
			locale,
			...layout,
			isDarkSearch: true,
			preview
		},
		revalidate: 60 * 2
	};
}

async function getAllNews(locale, preview) {
	const newsMeta = await getPages(NEWS_META_TYPE, NEWS_META_QUERY, locale, '', preview);
	const newsCount = newsMeta?.count || 0

	const allNews = []
	const batchSize = 500
	let skip = 0

	while (skip < newsCount) {
		const news = await getPagesWithVariables(ALL_NEWS_TYPE, ALL_NEWS_QUERY, {locale, skip}, '', preview);
		allNews.push(...news);
		skip += batchSize;
	}

	return allNews
}
